import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, DataAsOf } from "@/components/widgets";
import { ComplaintDrawer } from "@/components/ComplaintDrawer";
import { toast } from "sonner";
import { AlertTriangle, Award } from "lucide-react";
import { cn } from "@/lib/utils";
import { PanelCard, DashboardSkeleton, PeriodToggle } from "@/components/dashboard/primitives";
import { KpiTiles, OemStrip, AgingFunnel, FnSplit, TopCategories } from "@/components/dashboard/KpiSections";
import { ClosureLeaderboard, PreventionLeaderboard, HandlerLeaderboard, SaTable } from "@/components/dashboard/Leaderboards";
import { EscalationMatrixPanel, OldestOpenPanel, TrendPanel, OutlierWatch } from "@/components/dashboard/DrilldownPanels";
import { TeamAdoptionPanel } from "@/components/dashboard/TeamAdoptionPanel";
import { TrendInsightsPanel } from "@/components/dashboard/TrendInsights";
import { GoogleRatingsLeaderboard } from "@/components/dashboard/GoogleRatingsLeaderboard";
import { CxScorecardLeaderboard } from "@/components/oem/CxScorecardLeaderboard";
import { NegativeReviewsPanel } from "@/components/ratings/NegativeReviewsPanel";
import { computeShadeRuns, periodStartISO, teamLabel } from "@/components/dashboard/constants";

const LEADERSHIP_ROLES = ["admin", "md", "vp_sales", "vp_service", "cluster_head_sales", "cluster_head_service"];

export default function DashboardPage() {
  const { user, fnFilter, setFnFilter } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const effectiveFn = user?.function_scope !== "Both" ? user.function_scope : fnFilter;

  useEffect(() => {
    setLoading(true);
    api
      .get("/dashboard", { params: { function: effectiveFn, period } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [effectiveFn, period]);

  const k = data?.kpis_period;
  const oem = data?.oem_strip;
  const standings = data?.standings;
  const scopeTitle = useMemo(() => {
    if (user?.outlet_scope !== "all") return "Outlet Dashboard";
    if (user?.role === "vp_sales") return "Group Sales Dashboard";
    if (user?.role === "vp_service") return "Group Service Dashboard";
    return "Group Dashboard";
  }, [user]);

  const showSplit = Boolean(data?.function_split) && effectiveFn === "Both";
  const myOutletId = data?.user_outlet_id;

  // shaded runs where opened > closed
  const shadeRuns = useMemo(() => computeShadeRuns(data?.trend || []), [data]);

  if (loading || !data) return <DashboardSkeleton />;

  const openComplaint = (id) => { setDrawerId(id); setDrawerOpen(true); };
  const pl = k.period_label;
  const pStartISO = periodStartISO(period);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={scopeTitle}
        subtitle={`${effectiveFn === "Both" ? "Sales + Service" : effectiveFn} · OEM delays tracked separately · every number drills down`}
      >
        <PeriodToggle period={period} setPeriod={setPeriod} />
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-12 gap-3">
        {/* ---------- Row 1: periodized KPI tiles (accountable lens) ---------- */}
        <KpiTiles k={k} pl={pl} pStartISO={pStartISO} navigate={navigate} />

        {/* ---------- OEM delay strip (neutral, informational) ---------- */}
        <OemStrip oem={oem} navigate={navigate} />

        {/* ---------- Trend Insights (rules-based, verifiable) ---------- */}
        <TrendInsightsPanel data={data.insights} navigate={navigate} />

        {/* ---------- MD-only: Team Adoption at a glance ---------- */}
        {user?.role === "md" && <TeamAdoptionPanel />}

        {/* ---------- Row 2: Aging & Mix ---------- */}
        <PanelCard title="Aging Funnel — open complaints" testId="aging-funnel-panel"
          className={cn("col-span-2 order-4 lg:order-none", showSplit ? "lg:col-span-4" : "lg:col-span-6")}>
          <AgingFunnel funnel={data.aging_funnel} navigate={navigate} />
        </PanelCard>

        {showSplit && (
          <PanelCard title="Sales vs Service Split" testId="fn-split-panel"
            className="col-span-2 order-10 lg:order-none lg:col-span-4">
            <FnSplit split={data.function_split} navigate={navigate} setFnFilter={setFnFilter} />
          </PanelCard>
        )}

        <PanelCard title={`Top 5 Categories — ${data.top_categories_month.label}`} testId="top-categories-panel"
          className={cn("col-span-2 order-11 lg:order-none", showSplit ? "lg:col-span-4" : "lg:col-span-6")}>
          <TopCategories cats={data.top_categories_month} navigate={navigate} />
        </PanelCard>

        {/* ---------- Row 3: This Month's Standings ---------- */}
        <div className="col-span-2 lg:col-span-12 order-5 lg:order-none flex items-center gap-2 mt-1 flex-wrap" data-testid="standings-header">
          <Award size={17} className="text-[hsl(var(--cx-gold))]" />
          <h2 className="font-serif text-lg text-[hsl(var(--cx-ink))]">This Month's Standings — {standings.month_label}</h2>
          <span className="text-xs text-muted-foreground hidden sm:inline">· genuine closures only — a reopened case loses its closure credit</span>
          <DataAsOf className="ml-auto" />
        </div>

        <ClosureLeaderboard rows={standings.closure} myOutletId={myOutletId} navigate={navigate} />

        <PreventionLeaderboard prevention={standings.prevention} myOutletId={myOutletId} navigate={navigate} />

        <HandlerLeaderboard standings={standings} navigate={navigate} />

        {/* ---------- Google Ratings leaderboard (recognition only) ---------- */}
        <GoogleRatingsLeaderboard boards={data.google_leaderboard} myOutletId={myOutletId} navigate={navigate} />

        {/* ---------- Kia OEM CX scorecard leaderboard (fifth board) ---------- */}
        <CxScorecardLeaderboard board={standings.cx} myOutletId={myOutletId} navigate={navigate} />

        {/* ---------- Leadership: negative Google review early-warning ---------- */}
        {LEADERSHIP_ROLES.includes(user?.role) && (
          <NegativeReviewsPanel compact className="col-span-2 order-[5.5] lg:order-none lg:col-span-12" />
        )}

        {/* ---------- Row 4: Escalations + Oldest ---------- */}
        <EscalationMatrixPanel matrix={data.escalation_matrix} navigate={navigate} />

        <OldestOpenPanel items={data.oldest_open} openComplaint={openComplaint} />

        {/* ---------- Row 5: Trend + Outliers ---------- */}
        <TrendPanel trend={data.trend} shadeRuns={shadeRuns} navigate={navigate} />

        <OutlierWatch outliers={data.outliers} navigate={navigate} />

        {/* Bottom performers (backlog accountability, all open complaints) */}
        {data.sa_accountability?.length > 0 && (
          <PanelCard
            title={<span className="flex items-center gap-1.5"><AlertTriangle size={15} className="text-[hsl(var(--cx-crimson))]" />Bottom Performers — Most Open &amp; Overdue Complaints</span>}
            right={<DataAsOf />}
            testId="sa-accountability-card" className="col-span-2 order-[15] lg:order-none lg:col-span-12">
            <p className="px-5 pb-2 text-[0.68rem] text-muted-foreground">
              {teamLabel(effectiveFn)} carrying
              the largest backlog of open red (5+ days) complaints — regardless of month. Click a row to see their open cases.
            </p>
            <SaTable rows={data.sa_accountability} navigate={navigate} />
          </PanelCard>
        )}
      </div>

      <ComplaintDrawer complaintId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
